<script setup>
import {ref} from "vue";
import debounce from "lodash-es/debounce.js";
import {route} from "ziggy-js";
import PickerPopover from "@/Components/CommunityChat/PickerPopover.vue";
import Spinner from "@/Components/Icons/Spinner.vue";

const emit = defineEmits(['pick'])

const query = ref('')
const results = ref([])
const isLoading = ref(false)
const isUnavailable = ref(false)
const hasSearched = ref(false)

let controller = null

const runSearch = (q) => {
    if (controller) controller.abort()
    const current = new AbortController()
    controller = current

    isLoading.value = true
    isUnavailable.value = false

    axios.get(route('chat.gifs.search'), {params: {q}, signal: current.signal}).then(({data}) => {
        if (controller !== current) return
        results.value = data.data || []
        hasSearched.value = true
    }).catch((error) => {
        if (axios.isCancel(error) || controller !== current) return
        if (error.response?.status === 503) isUnavailable.value = true
        results.value = []
    }).finally(() => {
        if (controller === current) isLoading.value = false
    })
}

const debouncedSearch = debounce(() => runSearch(query.value.trim()), 400)

const onOpen = () => {
    if (!hasSearched.value) runSearch('')
}

const pick = (gif, close) => {
    emit('pick', gif)
    close()
}
</script>

<template>
    <PickerPopover @open="onOpen">
        <template #trigger>
            <img src="/assets/icons/gif.svg" alt="" class="w-6 h-6"/>
        </template>
        <template #default="{close}">
            <div class="w-72 max-w-full flex flex-col">
                <div class="p-2 border-b border-slate-200 dark:border-slate-700">
                    <input v-model="query" @input="debouncedSearch" type="text"
                           placeholder="Search KLIPY"
                           class="w-full text-sm rounded-md border border-slate-300 dark:border-slate-700 bg-white dark:bg-dark-blue dark:text-slate-200 px-2 py-1.5 focus:ring-0 focus:outline-none"/>
                </div>
                <div class="h-72 overflow-y-auto p-2">
                    <div v-if="isLoading" class="flex items-center justify-center h-full">
                        <Spinner/>
                    </div>
                    <div v-else-if="isUnavailable" class="flex items-center justify-center h-full text-center text-xs text-slate-400 px-4">
                        {{ $t('GIF search is temporarily unavailable') }}
                    </div>
                    <div v-else-if="!results.length" class="flex items-center justify-center h-full text-xs text-slate-400">
                        {{ $t('No GIFs found') }}
                    </div>
                    <div v-else class="grid grid-cols-2 gap-1.5">
                        <button v-for="gif in results" :key="gif.id" type="button"
                                class="rounded overflow-hidden aspect-square bg-slate-100 dark:bg-slate-800"
                                @click="pick(gif, close)">
                            <img :src="gif.preview_url" :alt="gif.title" loading="lazy" class="w-full h-full object-cover"/>
                        </button>
                    </div>
                </div>
                <!-- Required by KLIPY's attribution guidelines for the free API tier. -->
                <div class="px-2 py-1.5 border-t border-slate-200 dark:border-slate-700 text-[10px] text-slate-400 text-center">
                    Powered by KLIPY
                </div>
            </div>
        </template>
    </PickerPopover>
</template>
